import React, { useCallback, useRef, useState, useEffect } from 'react';

/**
 * MultiTouch-optimierter Button für Tablets und Smartphones
 * Unterstützt gleichzeitige Touch-Events ohne Interferenz
 */
const MultiTouchButton = ({
    text,
    type = 'default',
    onClick,
    disabled = false,
    width = '100%',
    height = 'auto',
    className = '',
    icon = null,
    hint = '',
    style = {},
    preventTouchInterference = true
}) => {
    const buttonRef = useRef(null);
    const [isPressed, setIsPressed] = useState(false);
    const [touchId, setTouchId] = useState(null);

    // Verhindert Event-Bubbling und Touch-Interferenz
    const handleTouchStart = useCallback((e) => {
        if (disabled || touchId !== null) return;

        if (preventTouchInterference) {
            e.preventDefault();
            e.stopPropagation();
        }

        const touch = e.changedTouches[0];
        setTouchId(touch.identifier);
        setIsPressed(true);
    }, [disabled, touchId, preventTouchInterference]);

    const handleTouchEnd = useCallback((e) => {
        if (disabled) return;

        if (preventTouchInterference) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Prüfe ob der richtige Touch beendet wurde
        const touch = Array.from(e.changedTouches).find(t => t.identifier === touchId);
        if (touch && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const isInsideButton = 
                touch.clientX >= rect.left &&
                touch.clientX <= rect.right &&
                touch.clientY >= rect.top &&
                touch.clientY <= rect.bottom;

            if (isInsideButton && onClick) {
                onClick(e);
            }
        }

        setIsPressed(false);
        setTouchId(null);
    }, [disabled, touchId, onClick, preventTouchInterference]);

    const handleTouchMove = useCallback((e) => {
        if (disabled || touchId === null) return;

        if (preventTouchInterference) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Prüfe ob der Touch noch innerhalb des Buttons ist
        const touch = Array.from(e.changedTouches).find(t => t.identifier === touchId);
        if (touch && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const isInsideButton = 
                touch.clientX >= rect.left &&
                touch.clientX <= rect.right &&
                touch.clientY >= rect.top &&
                touch.clientY <= rect.bottom;

            setIsPressed(isInsideButton);
        }
    }, [disabled, touchId, preventTouchInterference]);

    const handleTouchCancel = useCallback((e) => {
        if (preventTouchInterference) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        setIsPressed(false);
        setTouchId(null);
    }, [preventTouchInterference]);

    // Fallback für Mouse-Events (Desktop)
    const handleMouseClick = useCallback((e) => {
        if (disabled) return;
        
        // Nur ausführen wenn kein Touch aktiv ist
        if (touchId === null && onClick) {
            onClick(e);
        }
    }, [disabled, touchId, onClick]);

    // Cleanup bei Unmount
    useEffect(() => {
        return () => {
            setIsPressed(false);
            setTouchId(null);
        };
    }, []);

    const buttonClasses = [
        'multitouch-button',
        `multitouch-button--${type}`,
        isPressed ? 'multitouch-button--pressed' : '',
        disabled ? 'multitouch-button--disabled' : '',
        className
    ].filter(Boolean).join(' ');

    const buttonStyle = {
        width,
        height,
        ...style
    };

    return (
        <button
            ref={buttonRef}
            className={buttonClasses}
            style={buttonStyle}
            disabled={disabled}
            title={hint}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            onTouchCancel={handleTouchCancel}
            onClick={handleMouseClick}
            // Verhindere Standard-Touch-Verhalten
            onContextMenu={(e) => e.preventDefault()}
        >
            <span className="multitouch-button__content">
                {icon && <span className="multitouch-button__icon">{icon}</span>}
                {text && <span className="multitouch-button__text">{text}</span>}
            </span>
        </button>
    );
};

export default MultiTouchButton;